import { useMemo, useState } from "react";
import { Icon, ICON_NAMES, type IconName } from "./Icon.tsx";
import type { OrderDrawerType } from "../modules/orders/OrderDrawer.tsx";
import {
  type AdminNotification,
  type AdminNotificationKind,
  useAdminNotifications,
  useMarkNotificationSeen,
} from "../lib/useAdminNotifications.ts";

export interface NotificationOrderRef {
  id: string;
  type: OrderDrawerType;
}

interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  /** Open the order behind a notification (one-click drill-in). */
  onOpenOrder?: (ref: NotificationOrderRef) => void;
}

// Notification ids encode the source order. Server format (adminNotification.
// service.ts, BUG-04 2026-05-30): "flex-new-<orderId>-<updatedAtMs>" и
// "shop-new-<orderId>-<updatedAtMs>" — хвостовой таймстамп (13 цифр) нужно
// срезать, иначе drill-in бьёт в GET с id вида "<uuid>-<ms>" → 404.
// Срез безопасен: последний сегмент uuid — 12 hex-знаков, сам orderId
// оканчиваться на "-<13 цифр>" не может; легаси-id без таймстампа проходят
// как есть. Cash-audit / system ids carry no order — non-navigable.
// "urgent-rejected-<id>" (Ozon/WB intake) больше не навигабелен: модуль Ozon/WB
// удалён 2026-06-12 — архив в ветке archive/ozonwb-flow (тег pre-ozonwb-extract).
function stripNotificationTimestamp(raw: string): string {
  const m = raw.match(/^(.+)-\d{13}$/);
  return m?.[1] ?? raw;
}

export function parseNotificationOrderRef(id: string): NotificationOrderRef | null {
  if (id.startsWith("flex-new-"))
    return { type: "flex", id: stripNotificationTimestamp(id.slice("flex-new-".length)) };
  if (id.startsWith("shop-new-"))
    return { type: "shop", id: stripNotificationTimestamp(id.slice("shop-new-".length)) };
  return null;
}

type NotificationTab = "all" | "urgent" | "order";
type TimeGroup = "now" | "today" | "earlier";

// Касса убрана из источников уведомлений (пока что) — вкладку «Касса» не
// показываем. Компонент по-прежнему умеет рендерить kind="cash", если вернётся.
const TABS: Array<{ id: NotificationTab; label: string; kinds: AdminNotificationKind[] | null }> = [
  { id: "all", label: "Все", kinds: null },
  { id: "urgent", label: "Срочные", kinds: ["urgent"] },
  { id: "order", label: "Заказы", kinds: ["order"] },
];

const TIME_GROUPS: Array<{ id: TimeGroup; label: string }> = [
  { id: "now", label: "Сейчас" },
  { id: "today", label: "Сегодня" },
  { id: "earlier", label: "Ранее" },
];

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

function isIconName(icon: string): icon is IconName {
  return (ICON_NAMES as readonly string[]).includes(icon);
}

function toTimeGroup(notification: AdminNotification, now: number): TimeGroup {
  const createdAt = new Date(notification.createdAt).getTime();
  const age = now - createdAt;
  if (age < 30 * MINUTE_MS) return "now";
  if (age < 24 * HOUR_MS) return "today";
  return "earlier";
}

function formatTime(createdAt: string): string {
  return new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(createdAt));
}

export function NotificationsPanel({ open, onClose, onOpenOrder }: NotificationsPanelProps) {
  const [activeTab, setActiveTab] = useState<NotificationTab>("all");
  const { data: notifications = [], isLoading, isError } = useAdminNotifications();
  const markSeen = useMarkNotificationSeen();

  const grouped = useMemo(() => {
    const active = TABS.find((tab) => tab.id === activeTab) ?? { id: "all", label: "Все", kinds: null };
    const now = Date.now();
    const buckets: Record<TimeGroup, AdminNotification[]> = {
      now: [],
      today: [],
      earlier: [],
    };

    for (const notification of notifications) {
      if (active.kinds && !active.kinds.includes(notification.kind)) continue;
      buckets[toTimeGroup(notification, now)].push(notification);
    }

    return buckets;
  }, [activeTab, notifications]);

  if (!open) return null;

  return (
    <div className="notifications-layer" role="presentation">
      <button
        className="notifications-scrim"
        type="button"
        aria-label="Закрыть уведомления"
        onClick={onClose}
        data-testid="notifications-scrim"
      />
      <aside className="notifications-panel" aria-label="Уведомления">
        <div className="notifications-head">
          <div>
            <h2>Уведомления</h2>
            <p>События за последние сутки</p>
          </div>
          <button className="notifications-close" type="button" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="notifications-tabs" role="tablist" aria-label="Фильтр уведомлений">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="notifications-body">
          {isLoading && <div className="notifications-state">Загрузка...</div>}
          {isError && <div className="notifications-state">Не удалось загрузить уведомления</div>}
          {!isLoading && !isError && TIME_GROUPS.map((group) => {
            const items = grouped[group.id];
            if (items.length === 0) return null;

            return (
              <section className="notification-group" key={group.id} data-testid={`notification-group-${group.id}`}>
                <h3>{group.label}</h3>
                <div className="notification-list">
                  {items.map((notification) => {
                    const iconName = isIconName(notification.icon) ? notification.icon : "bell";
                    const orderRef = parseNotificationOrderRef(notification.id);
                    const navigable = !!(orderRef && onOpenOrder);
                    return (
                      <article
                        className="notification-item"
                        data-state={notification.unread ? "unseen" : "seen"}
                        data-unread={notification.unread ? "true" : undefined}
                        data-navigable={navigable ? "true" : undefined}
                        key={notification.id}
                        role={navigable ? "button" : undefined}
                        tabIndex={navigable ? 0 : undefined}
                        style={navigable ? { cursor: "pointer" } : undefined}
                        title={navigable ? "Открыть заказ" : undefined}
                        onMouseEnter={() => {
                          if (notification.unread) markSeen.mutate(notification.id);
                        }}
                        onClick={() => {
                          if (notification.unread) markSeen.mutate(notification.id);
                          if (orderRef && onOpenOrder) onOpenOrder(orderRef);
                        }}
                        onKeyDown={(e) => {
                          if (navigable && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            if (notification.unread) markSeen.mutate(notification.id);
                            if (orderRef && onOpenOrder) onOpenOrder(orderRef);
                          }
                        }}
                      >
                        <span className={`notification-icon notification-icon-${notification.kind}`}>
                          <Icon name={iconName} size={17} aria-hidden="true" />
                        </span>
                        <div className="notification-copy">
                          <div className="notification-title-row">
                            <h4>{notification.title}</h4>
                            <time dateTime={notification.createdAt}>{formatTime(notification.createdAt)}</time>
                          </div>
                          <p>{notification.body}</p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
          {!isLoading && !isError && Object.values(grouped).every((items) => items.length === 0) && (
            <div className="notifications-state">Нет уведомлений</div>
          )}
        </div>
      </aside>
    </div>
  );
}
